import { useState, useEffect, lazy, Suspense } from 'react';
import { useNavigate } from 'react-router-dom';
import { Header } from '@/components/Header';
import { HeroSectionObrim } from '@/components/blocks/hero-section-obrim';
import { ScrollIndicator } from '@/components/ScrollIndicator';
import { Footer } from '@/components/Footer';
import { useAuth } from '@/components/AuthProvider';
import { useToast } from '@/hooks/use-toast';
import { ExitIntentPopup } from '@/components/ExitIntentPopup';
import { WhatsAppWidget } from '@/components/WhatsAppWidget';
import { MobileStickyCTA } from '@/components/MobileStickyCTA';
import { useUTMTracking } from '@/hooks/useUTMTracking';

// Lazy load seções pesadas abaixo da dobra para LCP < 1.2s
const SolutionsSection = lazy(() => import('@/components/SolutionsSection').then(m => ({ default: m.SolutionsSection })));
const BenefitsSection = lazy(() => import('@/components/BenefitsSection').then(m => ({ default: m.BenefitsSection })));
const PricingSection = lazy(() => import('@/components/PricingSection').then(m => ({ default: m.PricingSection })));
const FAQSection = lazy(() => import('@/components/FAQSection').then(m => ({ default: m.FAQSection })));
const CTASection = lazy(() => import('@/components/CTASection').then(m => ({ default: m.CTASection })));

// Loading fallback minimalista
const SectionLoader = () => (
  <div className="py-16 flex items-center justify-center">
    <div className="w-8 h-8 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
  </div>
);

const Index = () => {
  const [darkMode, setDarkMode] = useState(false);
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { trackEvent } = useUTMTracking();

  // Track page view
  useEffect(() => {
    trackEvent('PageView', { page: 'home' });
  }, []);

  useEffect(() => {
    const saved = localStorage.getItem('darkMode');
    const systemPrefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;

    if (saved) {
      setDarkMode(saved === 'true');
    } else {
      setDarkMode(systemPrefersDark);
    }
  }, []);

  useEffect(() => {
    localStorage.setItem('darkMode', darkMode.toString());
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [darkMode]);

  useEffect(() => {
    if (!loading && user) {
      toast({
        title: "Bem-vindo de volta!",
        description: "Redirecionando para o dashboard...",
        duration: 2000,
      });

      navigate('/dashboard', { replace: true });
    }
  }, [user, loading, navigate, toast]);

  return (
    <div className="min-h-screen bg-background">
      <ScrollIndicator />
      <Header darkMode={darkMode} setDarkMode={setDarkMode} />
      <main>
        {/* Critical - carrega imediatamente */}
        <HeroSectionObrim />

        {/* Non-critical - lazy loaded */}
        <Suspense fallback={<SectionLoader />}>
          <SolutionsSection />
        </Suspense>
        <Suspense fallback={<SectionLoader />}>
          <BenefitsSection />
        </Suspense>
        <Suspense fallback={<SectionLoader />}>
          <PricingSection />
        </Suspense>
        <Suspense fallback={<SectionLoader />}>
          <FAQSection />
        </Suspense>
        <Suspense fallback={<SectionLoader />}>
          <CTASection />
        </Suspense>
      </main>
      <Footer />
      
      {/* Growth Widgets */}
      <ExitIntentPopup enabled={!user} />
      <WhatsAppWidget phoneNumber="5511999999999" />
      <MobileStickyCTA />
    </div>
  );
};

export default Index;
